import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
  alpha,
  Avatar,
  Box,
  Divider,
  IconButton,
  Link,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { darken } from "@mui/material/styles";
import Logo from "app/components/Logo/Logo";
import { useAppSelector } from "app/hooks";
import { getMonogram, Spacer, Toolbar, useDownloadUrl } from "common";
import { selectProfile } from "features/profile/profileSlice";
import I18n from "i18n";
import { ReactNode } from "react";
import WangoghLogo from "wangogh/WangoghLogo";

export interface SidebarShellV2Props {
  open?: boolean;
  temporary?: boolean;
  setOpen?: (open?: boolean) => void;
  navigation: ReactNode;
  userMenu: ReactNode;
  profileSubtitle?: ReactNode;
  beforeProfile?: ReactNode;
}

export default function SidebarShellV2({
  open = false,
  temporary = false,
  setOpen,
  navigation,
  userMenu,
  profileSubtitle,
  beforeProfile,
}: SidebarShellV2Props) {
  const profile = useAppSelector(selectProfile);
  const { downloadUrl } = useDownloadUrl();

  return (
    <Paper
      className="AppSidebarWrapper"
      square
      sx={{ minHeight: 0, overflow: "hidden" }}
    >
      {!temporary && (
        <>
          <Toolbar
            sx={{
              minHeight: 72,
              px: open ? 2 : 0,
              justifyContent: open ? "flex-start" : "center",
              overflow: "hidden",
            }}
          >
            <Logo size={40} />
            {open && (
              <Typography variant="h6" noWrap sx={{ ml: 1.5, fontWeight: 700 }}>
                {I18n.t("App.title")}
              </Typography>
            )}
          </Toolbar>
          <Divider sx={{ borderColor: alpha("#fff", 0.14) }} />
        </>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: "hidden",
          overflowY: "auto",
          py: 1,
          overscrollBehavior: "contain",
          scrollbarGutter: open ? "stable" : "auto",
          scrollbarWidth: open ? "auto" : "none",
          ...(!open && {
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }),
        }}
      >
        {navigation}
      </Box>

      <Box sx={{ flexShrink: 0, px: open ? 1 : 0.75, pb: 1 }}>
        {open && beforeProfile}

        {!temporary && (
          <Box
            sx={{
              display: "flex",
              justifyContent: open ? "flex-end" : "center",
            }}
          >
            <Tooltip
              title={
                open ? I18n.t("Sidebar.collapse") : I18n.t("Sidebar.expand")
              }
              placement="right"
            >
              <IconButton
                onClick={() => setOpen?.(!open)}
                color="inherit"
                aria-label={
                  open ? I18n.t("Sidebar.collapse") : I18n.t("Sidebar.expand")
                }
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: theme =>
                    alpha(theme.palette.common.white, 0.08),
                  "&:hover": {
                    backgroundColor: theme =>
                      alpha(theme.palette.common.white, 0.16),
                  },
                }}
              >
                {open ? (
                  <ChevronLeftRoundedIcon />
                ) : (
                  <ChevronRightRoundedIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minHeight: 56,
            mt: 0.5,
            px: open ? 0.5 : 0,
            justifyContent: open ? "flex-start" : "center",
          }}
        >
          <Avatar
            sx={{
              width: 38,
              height: 38,
              flexShrink: 0,
              backgroundColor: theme => alpha(theme.palette.primary.dark, 0.7),
              color: "common.white",
            }}
            src={profile.file_id ? downloadUrl(profile.file_id) : undefined}
          >
            {getMonogram(profile)}
          </Avatar>
          {open && (
            <>
              <Box sx={{ minWidth: 0, ml: 1 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {profile.last_name} {profile.first_name}
                </Typography>
                <Typography
                  component="div"
                  variant="body2"
                  noWrap
                  fontSize={10}
                  sx={{ color: theme => theme.palette.text.secondary }}
                >
                  {profileSubtitle}
                </Typography>
              </Box>
              <Spacer right />
              {userMenu}
            </>
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: alpha("#fff", 0.14) }} />
      <Box
        sx={{
          minHeight: 44,
          px: open ? 2 : 0,
          display: "flex",
          alignItems: "center",
          justifyContent: open ? "flex-end" : "center",
        }}
      >
        <Link
          href="https://www.wangoghsoft.com"
          target="_blank"
          rel="noreferrer"
          underline="none"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: theme => darken(theme.palette.primary.dark, 0.4),
          }}
        >
          {open && (
            <Typography variant="body2" fontSize={10}>
              created by
            </Typography>
          )}
          <WangoghLogo sx={{ width: 36 }} typo={false} />
        </Link>
      </Box>
    </Paper>
  );
}
